import { useEffect, useRef, useCallback, useState } from 'react';

const ANALYTICS_API = '/api/analytics_track.php';

const DEBUG = process.env.NODE_ENV === 'development';

const VISITOR_UUID_KEY = 'mm_visitor_uuid';
const SESSION_UUID_KEY = 'mm_session_uuid';
const CONSENT_COOKIE = 'mm_analytics_consent';
const RECENT_EVENT_IDS_KEY = 'mm_recent_event_ids';
const RECENT_EVENT_WINDOW_MS = 30000;

const ALLOWED_EVENT_TYPES = new Set([
  'app_opened',
  'page_visited',
  'route_changed',
  'category_visited',
  'search_performed',
  'filters_applied',
  'pagination_changed',
  'session_started',
  'session_expired',
  'product_card_clicked',
  'product_modal_opened',
  'product_viewed',
  'product_gallery_interacted',
  'product_saved',
  'product_unsaved',
  'product_added_to_basket',
  'product_quantity_changed',
  'product_removed_from_basket',
  'basket_opened',
  'quote_request_initiated',
  'quote_request_submitted',
]);

const SENSITIVE_KEYS = [
  'password', 'token', 'email', 'phone', 'address', 'credit', 'card',
  'cvv', 'ssn', 'id_number', 'auth', 'session',
];

function generateUuid() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return Math.random().toString(36).substr(2, 16) + Date.now().toString(36);
}

function getConsent() {
  if (typeof document === 'undefined') return null;
  const match = document.cookie.match(new RegExp('(^| )' + CONSENT_COOKIE + '=([^;]+)'));
  return match ? match[2] : null;
}

function setConsentCookie(value) {
  if (typeof document === 'undefined') return;
  const maxAge = 365 * 24 * 60 * 60;
  document.cookie = `${CONSENT_COOKIE}=${value}; path=/; max-age=${maxAge}; SameSite=Lax`;
}

function sanitizePath(path) {
  try {
    const url = new URL(path, window.location.origin);
    return url.pathname;
  } catch {
    return String(path || '').slice(0, 500);
  }
}

function sanitizeEventData(data) {
  if (!data || typeof data !== 'object') return data || {};
  const result = {};
  for (const [key, value] of Object.entries(data)) {
    const lowerKey = String(key).toLowerCase();
    if (SENSITIVE_KEYS.some((s) => lowerKey.includes(s))) continue;
    result[key] = value;
  }
  return result;
}

export default function useAnalytics() {
  const [consent, setConsentState] = useState(() => getConsent());
  const visitorUuidRef = useRef(null);
  const sessionUuidRef = useRef(null);
  const recentSignaturesRef = useRef(new Set());
  const isReadyRef = useRef(false);

  useEffect(() => {
    let visitorUuid = localStorage.getItem(VISITOR_UUID_KEY);
    if (!visitorUuid) {
      visitorUuid = generateUuid();
      try {
        localStorage.setItem(VISITOR_UUID_KEY, visitorUuid);
      } catch {}
    }
    visitorUuidRef.current = visitorUuid;

    let sessionUuid = sessionStorage.getItem(SESSION_UUID_KEY);
    if (!sessionUuid) {
      sessionUuid = generateUuid();
      try {
        sessionStorage.setItem(SESSION_UUID_KEY, sessionUuid);
      } catch {}
    }
    sessionUuidRef.current = sessionUuid;

    const stored = sessionStorage.getItem(RECENT_EVENT_IDS_KEY);
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        const now = Date.now();
        const fresh = parsed.filter((e) => now - e.ts < RECENT_EVENT_WINDOW_MS);
        recentSignaturesRef.current = new Set(fresh.map((e) => e.id));
        sessionStorage.setItem(RECENT_EVENT_IDS_KEY, JSON.stringify(fresh));
      } catch {}
    }

    isReadyRef.current = true;

    trackEventRaw('app_opened', {});
    trackEventRaw('session_started', {});

    return () => {};
  }, []);

  useEffect(() => {
    const checkConsent = () => setConsentState(getConsent());
    checkConsent();
    const interval = setInterval(checkConsent, 5000);
    return () => clearInterval(interval);
  }, []);

  const trackEventRaw = useCallback(
    (eventType, payload = {}) => {
      if (!isReadyRef.current) {
        if (DEBUG) console.log('[Analytics] skipped: not ready', eventType);
        return;
      }
      if (!consent || consent !== 'granted') {
        if (DEBUG) console.log('[Analytics] skipped: consent not granted', consent, eventType);
        return;
      }
      if (!ALLOWED_EVENT_TYPES.has(eventType)) {
        if (DEBUG) console.log('[Analytics] skipped: unsupported event type', eventType);
        return;
      }

      if (DEBUG) {
        const logPayload = { eventType, payload };
        console.log('[Analytics] sending event', eventType, logPayload);
      }

      const signature = `${eventType}:${payload.productId || ''}:${payload.listingNumber || ''}:${sanitizePath(payload.page_path || window.location.pathname)}`;
      const now = Date.now();
      // Never deduplicate page_visited events — every navigation must be recorded.
      if (eventType === 'page_visited') {
        recentSignaturesRef.current.add(signature);
      } else if (recentSignaturesRef.current.has(signature)) {
        return;
      } else {
        recentSignaturesRef.current.add(signature);
      }

      try {
        const stored = sessionStorage.getItem(RECENT_EVENT_IDS_KEY);
        let entries = [];
        if (stored) {
          try {
            entries = JSON.parse(stored);
          } catch {}
        }
        entries.push({ id: signature, ts: now });
        const fresh = entries.filter((e) => now - e.ts < RECENT_EVENT_WINDOW_MS);
        sessionStorage.setItem(RECENT_EVENT_IDS_KEY, JSON.stringify(fresh));
      } catch {}

      const safePayload = sanitizeEventData(payload);
      const pagePath = sanitizePath(safePayload.page_path || window.location.pathname + window.location.search);

      const body = {
        visitor_uuid: visitorUuidRef.current,
        session_uuid: sessionUuidRef.current,
        event_type: eventType,
        event_id: generateUuid(),
        page_path: pagePath,
        page_title: safePayload.page_title || document.title || '',
        product_id: safePayload.productId ?? null,
        listing_type: safePayload.listingType ?? '',
        listing_number: safePayload.listingNumber ?? '',
        category_id: safePayload.categoryId ?? null,
        category_name: safePayload.categoryName ?? '',
        product_name: safePayload.productName ?? '',
        event_data: safePayload.eventData ?? {},
        client_timestamp: new Date().toISOString(),
      };

      Object.keys(body).forEach((key) => {
        if (body[key] === '' || body[key] === null) delete body[key];
      });

      fetch(ANALYTICS_API, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body),
      })
        .then(async (res) => {
          if (DEBUG) {
            let responseText = '';
            try { responseText = await res.text(); } catch {}
            console.log('[Analytics] response', res.status, responseText.slice(0, 200));
          }
          try {
            const data = await res.clone ? res.clone() : res;
            const json = await data.json();
            if (json && json.new_visitor_for_auth && json.visitor_uuid) {
              visitorUuidRef.current = json.visitor_uuid;
              try {
                localStorage.setItem(VISITOR_UUID_KEY, json.visitor_uuid);
              } catch {}
            }
          } catch {}
        })
        .catch((err) => {
          if (DEBUG) console.warn('[Analytics] fetch failed:', err.message);
        });
    },
    [consent]
  );

  const trackEvent = useCallback(
    (eventType, payload = {}) => {
      trackEventRaw(eventType, payload);
    },
    [trackEventRaw]
  );

  const grantConsent = useCallback(() => {
    setConsentCookie('granted');
    setConsentState('granted');
    trackEventRaw('session_started', {});
  }, [trackEventRaw]);

  const declineConsent = useCallback(() => {
    setConsentCookie('declined');
    setConsentState('declined');
  }, []);

  const withdrawConsent = useCallback(() => {
    setConsentCookie('withdrawn');
    setConsentState('withdrawn');
  }, []);

  const resetVisitor = useCallback(() => {
    try {
      localStorage.removeItem(VISITOR_UUID_KEY);
    } catch {}
    visitorUuidRef.current = null;
  }, []);

  return {
    trackEvent,
    consent,
    grantConsent,
    declineConsent,
    withdrawConsent,
    resetVisitor,
    visitorUuid: visitorUuidRef.current,
    sessionUuid: sessionUuidRef.current,
  };
}
